import React, { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import Field from "@/components/post-ad/Field";
import ConditionTiles from "@/components/post-ad/ConditionTiles";

/**
 * Slug helper for stable, kebab-case data-testids on schema-driven fields
 * (e.g. "Floor area (m²)" -> "floor-area-m").
 */
export const slugKey = (s) =>
  String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/** Serialise open-home slots to the "date → date · date → date" string. */
export function serializeOpenHomes(slots) {
  const valid = (slots || []).filter((s) => s.from && s.to);
  if (!valid.length) return "";
  const fmt = (v) => {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v;
    return d.toLocaleString("en-NZ", {
      day: "numeric", month: "long", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  };
  return valid.map((s) => `${fmt(s.from)} → ${fmt(s.to)}`).join(" · ");
}

function Chips({ value, onChange, options, testId, multi = false, coerceOption }) {
  // Normalise both sides so numeric-coerced state (e.g. 3) still matches
  // the raw schema string option ("3") for selected-state comparison.
  const norm = (v) => (coerceOption ? coerceOption(v) : v);
  const activeSet = multi ? new Set((Array.isArray(value) ? value : []).map(norm)) : null;
  const activeSingle = multi ? null : norm(value ?? "");
  return (
    <div
      className="flex flex-wrap gap-2"
      data-testid={testId}
      role={multi ? "group" : "radiogroup"}
    >
      {options.map((opt) => {
        const nOpt = norm(opt);
        const on = multi ? activeSet.has(nOpt) : activeSingle === nOpt;
        return (
          <button
            key={String(opt)}
            type="button"
            data-testid={`${testId}-opt-${slugKey(String(opt))}`}
            role={multi ? undefined : "radio"}
            aria-pressed={multi ? on : undefined}
            aria-checked={multi ? undefined : on}
            onClick={() => {
              if (multi) {
                const currentArr = (Array.isArray(value) ? value : []).map(norm);
                const has = currentArr.includes(nOpt);
                const next = has ? currentArr.filter((v) => v !== nOpt) : [...currentArr, nOpt];
                onChange(next);
              } else {
                onChange(on ? "" : nOpt);
              }
            }}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${
              on
                ? "bg-[color:var(--brand-blue)] text-white border-transparent"
                : "bg-white text-slate-700 border-soft hover:border-[color:var(--brand-blue)]"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function Toggle({ value, onChange, testId, defaultValue = false }) {
  const bool =
    typeof value === "boolean"
      ? value
      : value === "Yes" || value === true || value === "true"
      ? true
      : value === "No" || value === false || value === "false"
      ? false
      : defaultValue;
  return (
    <div className="flex gap-2" data-testid={testId}>
      {["Yes", "No"].map((label) => {
        const on = (label === "Yes") === bool;
        return (
          <button
            key={label}
            type="button"
            onClick={() => onChange(label === "Yes")}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${
              on
                ? "bg-[color:var(--brand-blue)] text-white border-transparent"
                : "bg-white text-slate-700 border-soft hover:border-[color:var(--brand-blue)]"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function AsyncSelect({ value, onChange, endpoint, options, testId, categorySlug }) {
  const q = useQuery({
    queryKey: ["schema-async-options", endpoint, categorySlug],
    queryFn: async () => {
      // Endpoint from backend is prefixed with "/api" — strip and delegate to sharedApi.
      const path = endpoint.replace(/^\/api/, "");
      const { data } = await sharedApi.get(path, {
        params: categorySlug ? { category_slug: categorySlug } : undefined,
      });
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.items)) return data.items;
      if (Array.isArray(data?.makes)) return data.makes;
      return [];
    },
    enabled: !!endpoint,
    staleTime: 10 * 60_000,
  });
  const opts = endpoint ? (q.data || []) : (options || []);
  const normalised = opts.map((o) =>
    typeof o === "string" ? { value: o, label: o } : { value: o.value || o.slug || o.name, label: o.label || o.name || o.value }
  );
  // When a schema-declared select has neither options_endpoint nor inline
  // options, fall back to a plain text input so any externally-prefilled
  // value (e.g. CarJam's `make: 'Toyota'`) can still be displayed and
  // edited by the seller. Prevents "select value not in options → blank"
  // UX bug caught in iteration_56.
  if (!endpoint && normalised.length === 0) {
    return (
      <input
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="input"
      />
    );
  }
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="input"
    >
      <option value="">—</option>
      {normalised.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function Slots({ value, onChange, testId }) {
  const slots = Array.isArray(value) ? value : [];
  const add = () => onChange([...slots, { from: "", to: "" }]);
  const remove = (i) => onChange(slots.filter((_, j) => j !== i));
  const update = (i, patch) =>
    onChange(slots.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  return (
    <div data-testid={testId}>
      <div className="space-y-2">
        {slots.map((s, i) => (
          <div
            key={i}
            data-testid={`${testId}-slot-${i}`}
            className="flex flex-wrap items-end gap-2 bg-white rounded-xl border border-soft p-2"
          >
            <label className="flex-1 min-w-[180px]">
              <span className="text-[10px] font-bold uppercase text-slate-400 block">From</span>
              <input
                type="datetime-local"
                value={s.from}
                onChange={(e) => update(i, { from: e.target.value })}
                className="input"
              />
            </label>
            <span className="text-slate-400 pb-2">→</span>
            <label className="flex-1 min-w-[180px]">
              <span className="text-[10px] font-bold uppercase text-slate-400 block">To</span>
              <input
                type="datetime-local"
                value={s.to}
                onChange={(e) => update(i, { to: e.target.value })}
                className="input"
              />
            </label>
            <button
              type="button"
              onClick={() => remove(i)}
              className="p-2 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              aria-label="Remove slot"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        data-testid={`${testId}-add`}
        className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-dashed border-[color:var(--brand-blue)] text-[color:var(--brand-blue)] px-3 py-1.5 text-xs font-semibold hover:bg-white transition-colors"
      >
        <Plus className="w-3.5 h-3.5" /> Add slot
      </button>
    </div>
  );
}

function ShippingRows({ value, onChange, max = 5, testId }) {
  const rows = Array.isArray(value) ? value : [];
  const add = () => rows.length < max && onChange([...rows, { label: "", price: 0 }]);
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  const update = (i, patch) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div data-testid={testId}>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div
            key={i}
            data-testid={`${testId}-row-${i}`}
            className="grid grid-cols-[1fr_120px_auto] gap-2 items-center bg-white rounded-xl border border-soft p-2"
          >
            <input
              value={r.label}
              onChange={(e) => update(i, { label: e.target.value })}
              className="input"
              placeholder="e.g. Overnight courier"
            />
            <input
              type="number"
              inputMode="decimal"
              min={0}
              value={r.price}
              onChange={(e) => update(i, { price: Number(e.target.value || 0) })}
              className="input"
              placeholder="0 = Free"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="p-2 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              aria-label="Remove shipping option"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      {rows.length < max && (
        <button
          type="button"
          onClick={add}
          data-testid={`${testId}-add`}
          className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-dashed border-[color:var(--brand-blue)] text-[color:var(--brand-blue)] px-3 py-1.5 text-xs font-semibold hover:bg-white transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add shipping option
        </button>
      )}
    </div>
  );
}

/**
 * Render a single schema-declared field. Wrapped in a Field label so it
 * plugs into the existing post-ad form styling. All values live in the
 * parent form state and are round-tripped verbatim through submit.
 */
export default function SchemaFieldRenderer({ field, value, onChange, categorySlug, source = "attr", prefilled = false }) {
  const testId = `post-ad-${source}-${slugKey(field.key)}`;
  const label = field.label || field.key;
  const required = !!field.required;
  const labelText = required ? `${label} *` : label;

  // Chips/multi that appear numeric coerce back to numbers on select so
  // schema-typed number attrs (e.g. Bedrooms, Bathrooms) submit as
  // integers, not strings. Property schema uses stringly-typed options.
  const coerceOption = (opt) => {
    if (typeof opt === "number") return opt;
    const trimmed = String(opt).trim();
    if (trimmed !== "" && !Number.isNaN(Number(trimmed)) && /^-?\d+(\.\d+)?$/.test(trimmed)) {
      return Number(trimmed);
    }
    return opt;
  };

  const inner = () => {
    switch (field.input) {
      case "text":
        return (
          <input
            data-testid={testId}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="input"
            maxLength={field.max || undefined}
            required={required}
          />
        );
      case "number":
        return (
          <input
            data-testid={testId}
            type="number"
            inputMode="decimal"
            min={field.min}
            max={field.max}
            value={value ?? ""}
            onChange={(e) =>
              onChange(e.target.value === "" ? "" : Number(e.target.value))
            }
            className="input"
            required={required}
          />
        );
      case "date":
        return (
          <input
            data-testid={testId}
            type="date"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="input"
            required={required}
          />
        );
      case "chips":
        return (
          <Chips
            value={value}
            onChange={onChange}
            options={field.options || []}
            testId={testId}
            coerceOption={coerceOption}
          />
        );
      case "multi":
        return (
          <Chips
            value={value}
            onChange={onChange}
            options={field.options || []}
            testId={testId}
            multi
            coerceOption={coerceOption}
          />
        );
      case "toggle":
        return (
          <Toggle
            value={value}
            onChange={onChange}
            testId={testId}
            defaultValue={field.default ?? false}
          />
        );
      case "select":
        return (
          <AsyncSelect
            value={value}
            onChange={onChange}
            endpoint={field.options_endpoint}
            options={field.options}
            testId={testId}
            categorySlug={categorySlug}
          />
        );
      case "tiles":
        return (
          <ConditionTiles
            value={value}
            onChange={onChange}
            testIdPrefix={testId}
            label=""
          />
        );
      case "slots":
        return <Slots value={value} onChange={onChange} testId={testId} />;
      case "shipping":
        return (
          <ShippingRows
            value={value}
            onChange={onChange}
            max={field.max || 5}
            testId={testId}
          />
        );
      default:
        return (
          <input
            data-testid={testId}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="input"
          />
        );
    }
  };

  return (
    <Field label={labelText} prefilled={prefilled}>
      {inner()}
      {field.hint && (
        <span className="text-[10px] text-slate-400 mt-1 block">{field.hint}</span>
      )}
    </Field>
  );
}

/**
 * Ensure defaults from the schema are seeded into the form state once,
 * so untouched toggles ship their `default` value on submit.
 */
export function useSeedSchemaDefaults(fields, get, set, keyPrefix = "") {
  useEffect(() => {
    if (!Array.isArray(fields) || fields.length === 0) return;
    fields.forEach((f) => {
      if (f.default === undefined) return;
      const current = get(`${keyPrefix}${f.key}`);
      if (current === undefined || current === "" || current === null) {
        set(`${keyPrefix}${f.key}`, f.default);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(fields?.map((f) => f.key + f.default))]);
}
